import React, { useState, useTransition } from "react";
import { useNavigate as useRouter } from "../../router";
import { Button } from "../../button";
import { Card, CardContent, CardHeader, CardTitle } from "../../card";
import { CheckboxField } from "../checkbox-field";
import { FormField } from "../form-shell";
import { Input } from "../../input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../select";
import { Textarea } from "../../textarea";
import { apiCall } from "../../api/client";
import {
  hiringJobs,
  type JobShape,
  type JobStatus,
} from "../../api/contracts/hiring-jobs";

interface JobFormProps {
  initial?: JobShape;
  /** Where to redirect on success. Defaults to `${basePath}/jobs/<slug>`. */
  redirectTo?: (job: JobShape) => string;
  /**
   * Base path for the post-save redirect, e.g. "/hiring" or
   * "/projects/<slug>/hiring". A string (not a function) so it can cross the
   * server→client boundary. Defaults to "/hiring".
   */
  basePath?: string;
}

const EMPLOYMENT_TYPES = [
  { value: "full_time", label: "Full-time" },
  { value: "part_time", label: "Part-time" },
  { value: "contract", label: "Contract" },
  { value: "intern", label: "Internship" },
];

const STATUSES: JobStatus[] = ["draft", "open", "closed", "archived"];

export function JobForm({
  initial,
  redirectTo,
  basePath = "/hiring",
}: JobFormProps) {
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    title: initial?.title ?? "",
    department: initial?.department ?? "",
    location: initial?.location ?? "",
    employment_type: initial?.employment_type ?? "full_time",
    description: initial?.description ?? "",
    salary_min: initial?.salary_min?.toString() ?? "",
    salary_max: initial?.salary_max?.toString() ?? "",
    salary_currency: initial?.salary_currency ?? "USD",
    status: initial?.status ?? ("draft" as JobStatus),
    is_public: initial?.is_public === 1,
    slug: initial?.slug ?? "",
  });

  function setField<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((prev) => ({ ...prev, [k]: v }));
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.title.trim()) {
      setError("Title is required.");
      return;
    }
    const payload = {
      title: form.title.trim(),
      department: form.department.trim() || null,
      location: form.location.trim() || null,
      employment_type: form.employment_type || null,
      description: form.description,
      salary_min: form.salary_min ? Number(form.salary_min) : null,
      salary_max: form.salary_max ? Number(form.salary_max) : null,
      salary_currency: form.salary_currency || null,
      status: form.status,
      is_public: form.is_public,
      slug: form.slug.trim() || undefined,
    };
    startTransition(async () => {
      try {
        const result = initial
          ? await apiCall(hiringJobs.update, {
              params: { id: initial.id },
              body: payload,
            })
          : await apiCall(hiringJobs.create, { body: payload });
        router.push(
          redirectTo ? redirectTo(result) : `${basePath}/jobs/${result.slug}`,
        );
        router.refresh();
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to save job.",
        );
      }
    });
  }

  return (
    <form className="space-y-4" onSubmit={onSubmit}>
      <Card>
        <CardHeader>
          <CardTitle className="type-title">Basics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <FormField label="Title" htmlFor="title" required>
            <Input
              id="title"
              value={form.title}
              required
              onChange={(e) => setField("title", e.target.value)}
              placeholder="Senior Engineer"
            />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Department" htmlFor="department">
              <Input
                id="department"
                value={form.department ?? ""}
                onChange={(e) => setField("department", e.target.value)}
                placeholder="Engineering"
              />
            </FormField>
            <FormField label="Location" htmlFor="location">
              <Input
                id="location"
                value={form.location ?? ""}
                onChange={(e) => setField("location", e.target.value)}
                placeholder="Remote / San Francisco"
              />
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Employment type">
              <Select
                value={form.employment_type ?? ""}
                onValueChange={(v) => setField("employment_type", v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent>
                  {EMPLOYMENT_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Status">
              <Select
                value={form.status}
                onValueChange={(v) => setField("status", v as JobStatus)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      <span className="capitalize">{s}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <FormField label="Salary min">
              <Input
                type="number"
                min="0"
                value={form.salary_min}
                onChange={(e) => setField("salary_min", e.target.value)}
                placeholder="120000"
              />
            </FormField>
            <FormField label="Salary max">
              <Input
                type="number"
                min="0"
                value={form.salary_max}
                onChange={(e) => setField("salary_max", e.target.value)}
                placeholder="180000"
              />
            </FormField>
            <FormField label="Currency">
              <Input
                maxLength={3}
                value={form.salary_currency ?? ""}
                onChange={(e) =>
                  setField("salary_currency", e.target.value.toUpperCase())
                }
                placeholder="USD"
              />
            </FormField>
          </div>
          {initial ? (
            <FormField
              label="Public slug"
              htmlFor="slug"
              help={`Public URL: /hiring/jobs/${form.slug || initial.slug}`}
            >
              <Input
                id="slug"
                value={form.slug}
                onChange={(e) => setField("slug", e.target.value)}
                placeholder="senior-engineer"
              />
            </FormField>
          ) : null}
          <CheckboxField
            label={
              <>Publish job page (will be visible at /hiring/jobs/&lt;slug&gt;)</>
            }
            checked={form.is_public}
            onCheckedChange={(v) => setField("is_public", v === true)}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="type-title">Description</CardTitle>
        </CardHeader>
        <CardContent>
          <FormField help="Markdown is rendered on the public job page (GitHub-flavored).">
            <Textarea
              className="min-h-60 font-mono"
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              placeholder={`## About the role\n\nWhat the candidate will do.\n\n## Requirements\n\n- 3+ years of …\n- Comfort with …`}
            />
          </FormField>
        </CardContent>
      </Card>

      {error ? <p className="type-body text-destructive">{error}</p> : null}
      <div className="flex items-center gap-2">
        <Button type="submit" disabled={pending}>
          {pending ? "Saving…" : initial ? "Save changes" : "Create job"}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => router.back()}
          disabled={pending}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
